import { ApolloError, ApolloQueryResult } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import { addStringListToKeySet, assertNever, MutableKeySet, stringListToKeySet } from '@tableau/data-structures';
import { VizPortalWebClientRequester } from '@tableau/vizportal-webclient-api';
import { ActivitySpinnerWidget } from '@tableau/widgets';
import React from 'react';
import { Messages } from '../../../../../gen-src/Localize';
import { SheetFieldInstanceDatasourceFragment, SheetFieldInstanceUpstreamDatabasesConnectionFragment, ViewDetailsByPathDashboardFragment, ViewDetailsByPathFragment, ViewDetailsByPathQuery, ViewDetailsByPathQueryVariables, ViewDetailsByPathSheetFragment, ViewDetailsByPathDocument } from '../../../../../gen-src/queries';
import {
  CenteredMessage,
  getFirstGraphQLErrorCode,
  ItemWithId,
  objectValues,
  TableauUser,
} from '../../../data-catalog-shared';
import { LoadingFailedWidget } from './LoadingFailedWidget';
import { SheetField } from './SheetField';
import { lineagePanelBodyWithError, LineagePanelContainer, loadingIndicator } from './ViewerPaneStyles';
import { VizClientDatabasesConnectionNode, VizClientDatasource, VizClientPublishedDatasource } from './VizClientInterfaces';
import { VizClientLineagePanelBody } from './VizClientLineagePanelBody';
import { VizClientLineagePaneTitleBar } from './VizClientLineagePanelTitleBar';

export type ViewDetailsByPathWrapperProps = {
  readonly viewPath: string;
  readonly testId: string;
  readonly onCloseRequested: () => void;
  readonly lastRefreshKey?: string;
  readonly vizPortalWebClientRequester: VizPortalWebClientRequester;
};

export type SheetFieldCollection = {
  readonly worksheetFields: ReadonlyArray<SheetField>;
  readonly datasourceFields: ReadonlyArray<SheetField>;
};

const emptySheetFields: SheetFieldCollection = {
  worksheetFields: [],
  datasourceFields: []
};

type DatasourceMap = { [key: string]: VizClientDatasource };

type Dashboard = ViewDetailsByPathDashboardFragment;
type Sheet = ViewDetailsByPathSheetFragment;
type View = ViewDetailsByPathFragment;

type RefetchType = (variables?: ViewDetailsByPathQueryVariables) => Promise<ApolloQueryResult<ViewDetailsByPathQuery>>;

export function getSheetFieldInstances(view: View): SheetFieldCollection {
  switch (view.__typename) {
    case 'Dashboard': return getSheetFieldInstancesFromDashboard(view);
    case 'Sheet': return getSheetFieldInstancesFromSheet(view);
    default: assertNever(view); return emptySheetFields;
  }
}

// TODO: move to DCS
function filterOutNullAndUndefined<T>(arr: ReadonlyArray<(T | null | undefined)> | (T | null | undefined)[]): ReadonlyArray<T> {
  return arr.filter(t => t !== null && t !== undefined) as ReadonlyArray<T>;
}

// TODO: move to DCS
function getIdsFromCollection(collection: (ReadonlyArray<ItemWithId | null>) | ((ItemWithId | null)[]) | null | undefined): string[] {
  if (!collection) { return []; }
  return filterOutNullAndUndefined(collection).map(t => t.id);
}


function getSheetFieldInstancesFromSheet(sheet: Sheet): SheetFieldCollection {
  if (!sheet.sheetFieldInstances) {
    return emptySheetFields;
  }

  const worksheetFields: SheetField[] = [];
  const datasourceFields: SheetField[] = [];


  const worksheetFieldIds = stringListToKeySet(getIdsFromCollection(sheet.worksheetFields));
  const datasourceFieldIds = stringListToKeySet(getIdsFromCollection(sheet.datasourceFields));

  sheet.sheetFieldInstances.forEach(field => {
    if (!field) { return; }

    if (datasourceFieldIds[field.id]) {
      datasourceFields.push(field);
    } else if (worksheetFieldIds[field.id]) {
      worksheetFields.push(field);
    }
  });

  return {
    worksheetFields: worksheetFields,
    datasourceFields: datasourceFields
  };
}

function getSheetFieldInstancesFromDashboard(dashboard: Dashboard): SheetFieldCollection {
  if (!dashboard.upstreamSheetFieldInstances) {
    return emptySheetFields;
  }

  const worksheetFields: SheetField[] = [];
  const datasourceFields: SheetField[] = [];

  const worksheetFieldIds: MutableKeySet = {};
  const datasourceFieldIds: MutableKeySet = {};

  const fieldIdsSeen: MutableKeySet = {};

  dashboard.sheets.forEach(sheet => {
    addStringListToKeySet(worksheetFieldIds, getIdsFromCollection(sheet.worksheetFields));
    addStringListToKeySet(datasourceFieldIds, getIdsFromCollection(sheet.datasourceFields));
  });

  dashboard.upstreamSheetFieldInstances.forEach(field => {
    if (!field || fieldIdsSeen[field.id]) {
      return;
    }

    fieldIdsSeen[field.id] = true;

    if (datasourceFieldIds[field.id]) {
      datasourceFields.push(field);
    } else if (worksheetFieldIds[field.id]) {
      worksheetFields.push(field);
    }
  });

  return {
    worksheetFields,
    datasourceFields
  };
}

export function getDatasourcesListFromDatasourceFields(fields: ReadonlyArray<SheetField>): VizClientDatasource[] {
  // Get datasources from fields list. Store them in an associative array by ID and
  // collect the values later.
  const datasourceMap: DatasourceMap = {};

  if (fields) {
    fields.forEach(field => {
      if (!field) {
        return;
      }

      // If this is a DatasourceField, it's connecting to a published datasource.
      // Make a note of that.
      let publishedDatasource: VizClientPublishedDatasource | null = null;

      // hasPublishedDatasource is necessary to disambiguate between if a datasource has no
      // published datasource vs if the published datasource is obfuscated
      let hasPublishedDatasource = false;

      if (field.__typename === 'DatasourceField') {
        hasPublishedDatasource = true;
        const datasource = field.remoteField?.publishedDataSourceDetails;
        if (datasource) {
          publishedDatasource = { ...datasource } as VizClientPublishedDatasource;
        }
      }

      // Fields without defined data sources do not factor into the list of datasources.
      // If we see the same embedded data source multiple times, but we do not yet have a
      // published data source, parse the field again to try finding a published data source.
      if (field.datasource && !datasourceMapsToPublishedDatasource(datasourceMap, field.datasource.id)) {
        datasourceMap[field.datasource.id] =
          datasourceToVizClientDatasource(
            field.datasource,
            publishedDatasource,
            hasPublishedDatasource,
            field.upstreamDatabasesConnection ?? null);
      }
    });
  }

  // True if the a DatasourceMap contains a given key and the key maps to a value with a
  // published data source.
  function datasourceMapsToPublishedDatasource(map: DatasourceMap, id: string): boolean {
    if (!map[id]) {
      return false; // map does not contain the given key
    }

    return map[id].hasPublishedDatasource;
  }

  // Hard-coding the language because sort behavior in Tableau isn't typically expected
  // to change based on the browser locale. This isn't ideal and should be revisited if
  // it is found to cause customer pain.
  return objectValues(datasourceMap).sort((a, b) =>
    (a.name || '').localeCompare((b.name || ''), 'en', { sensitivity: 'base' }));
}

function datasourceToVizClientDatasource(
  datasource: SheetFieldInstanceDatasourceFragment,
  publishedDatasource: VizClientPublishedDatasource | null,
  hasPublishedDatasource: boolean,
  upstreamDatabasesConnection: SheetFieldInstanceUpstreamDatabasesConnectionFragment | null,
): VizClientDatasource {
  return {
    id: datasource.id,
    name: datasource.name ?? null,
    __typename: datasource.__typename,
    hasExtracts: datasource.hasExtracts,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    extractLastUpdateTime: datasource.extractLastUpdateTime,
    hasPublishedDatasource: hasPublishedDatasource,
    publishedDatasource: publishedDatasource,
    databasesConnectionNodes: upstreamDatabasesConnection?.nodes?.map(t => ({ ...t }) as VizClientDatabasesConnectionNode) ?? null,
  };
}

export class ViewDetailsByPathWrapper extends React.Component<ViewDetailsByPathWrapperProps> {
  public override render(): React.ReactNode {
    return (
      <Query<ViewDetailsByPathQuery, ViewDetailsByPathQueryVariables>
        query={ViewDetailsByPathDocument}
        variables={{ viewPath: this.props.viewPath }}
      >
        {({ loading, error, data, refetch }) => (
          <ViewDetailsByPathBody loading={loading} error={error} data={data} refetch={refetch} {...this.props} />
        )}
      </Query>
    );
  }
}

export type ViewDetailsByPathBodyProps = {
  readonly loading: boolean;
  readonly error: ApolloError | undefined;
  readonly data: ViewDetailsByPathQuery | undefined;
  readonly refetch: RefetchType;
} & ViewDetailsByPathWrapperProps;

export class ViewDetailsByPathBody extends React.Component<ViewDetailsByPathBodyProps> {
  public override render(): React.ReactNode {
    if (this.props.loading) {
      return this.loadingIndicator();
    }
    // Ignore the PERMISSIONS_MODE_SWITCHED error, which occurs by design in a particular
    // catalog configuration.
    if (this.props.error && getFirstGraphQLErrorCode(this.props.error) !== 'PERMISSIONS_MODE_SWITCHED') {
      return this.renderError(this.props.error, this.props.refetch);
    }

    return this.renderViewDetails(this.props.data);
  }

  private readonly titleBar = (): React.ReactNode => {
    return (
      <VizClientLineagePaneTitleBar
        title={Messages.DataDetailsPaneTitle()}
        onCloseRequested={this.props.onCloseRequested}
      />
    );
  }

  private readonly renderViewDetails = (data: ViewDetailsByPathQuery | undefined): React.ReactNode => {
    // Querying a sheet by its own path should return exactly 1 sheet, unless the graph is not fully populated.
    // All sheets are expected to have a workbook.
    if (!data
      || !data.views
      || data.views.length === 0
      || !data.views[0]
      || !data.views[0].workbook) {
      return this.renderIngestionInProgress();
    }

    const responseView = data.views[0];

    const { datasourceFields, worksheetFields } = getSheetFieldInstances(responseView);
    const datasources = getDatasourcesListFromDatasourceFields(datasourceFields);

    const { workbook } = data.views[0];
    if (!workbook) {
      return this.renderIngestionInProgress();
    }

    return (
      <LineagePanelContainer data-tb-test-id={this.props.testId}>
        {this.titleBar()}
        <VizClientLineagePanelBody
          viewId={responseView.id}
          viewName={responseView.name ?? null}
          viewType={responseView.__typename}
          workbookName={workbook.name ?? null}
          workbookVizportalUrlId={workbook.vizportalUrlId}
          projectName={workbook.projectName ?? null}
          projectVizportalUrlId={workbook.projectVizportalUrlId ?? null}
          owner={workbook.owner ? { ...workbook.owner } as TableauUser : null} // TODO: simplify when we move to monorepo
          datasources={datasources}
          datasourceFields={datasourceFields}
          worksheetFields={worksheetFields}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          modifiedDate={responseView.updatedAt}
          vizPortalWebClientRequester={this.props.vizPortalWebClientRequester}
          viewPath={this.props.viewPath}
        />
      </LineagePanelContainer>
    );
  }

  private readonly loadingIndicator = (): React.ReactNode => {
    return (
      <LineagePanelContainer data-tb-test-id={this.props.testId}>
        <div className={loadingIndicator} aria-busy='true'>
          <ActivitySpinnerWidget testId='lineage-loading-details' />
          <div className='lineage-loading-details-message'>{Messages.LoadingDataDetails()}</div>
        </div>
      </LineagePanelContainer>
    );
  }

  private readonly renderError = (error: ApolloError, refetch: RefetchType): React.ReactNode => {
    // If we received an empty list of errors, display a generic error.
    if (!error.graphQLErrors || error.graphQLErrors.length === 0) {
      return this.renderPaneWithError(Messages.DataDetailsGenericErrorDetails());
    }

    return (
      <LineagePanelContainer data-tb-test-id={this.props.testId}>
        {this.titleBar()}

        <LoadingFailedWidget
          key='loadingFailedWidget'
          errorCode={getFirstGraphQLErrorCode(error)}
          onRetryRequested={getFirstGraphQLErrorCode(error) === 'BACKFILL_RUNNING' ? undefined : () => refetch({ viewPath: this.props.viewPath })}
        />
      </LineagePanelContainer>
    );
  }

  private readonly renderIngestionInProgress = () => {
    return this.renderPaneWithError(Messages.ErrorItemNotFound());
  }

  // Display a pane with a single error centered.
  private readonly renderPaneWithError = (errorMessage: string) => {
    return (
      <LineagePanelContainer data-tb-test-id={this.props.testId}>
        <VizClientLineagePaneTitleBar
          title={Messages.Error()}
          onCloseRequested={this.props.onCloseRequested}
        />
        <div className={lineagePanelBodyWithError}>
          <CenteredMessage message={errorMessage} />
        </div>
      </LineagePanelContainer>
    );
  }
}
